import { Box, CircularProgress, Flex, Stack, Text } from "@chakra-ui/react"
import { useRecoilValue, useRecoilState } from "recoil"
import { Routes, Views } from "../../../constants"
import { roomSelector } from "../../../recoil/atoms/room"
import { viewSelector } from "../../../recoil/atoms/view"
import { useTranslation } from "react-i18next"
import useWindowSize from "../../../hooks/useWindowSize"
import { useBrowserType } from "../../../hooks/useBrowserType"
import { useHistory } from "react-router"
import { loadingRoomHashAtom } from "../../../recoil/atoms/shows"
import { addCurrentQueryParams } from "../../../util"
import { SDKContext } from "../../../context/SDKContext"
import axios from "src/shared/axios"
import useSearchParams from "src/hooks/useSearchParams"
import { mobilePurchaseJustCompletedAtom } from "src/recoil/atoms/plans"
import { useLocalStorage } from "src/hooks/useLocalStorage"
import { useValidateRoom } from "src/hooks/useValidateRoom"
import { joinButtonLoadingSelector } from "src/recoil/atoms/joinButton"
import { PlansLayout } from "./components/PlansLayout"
import { PlansButton } from "./components/PlansButton"
import { MobileSubscription } from "./components/MobileSubscription"
import { StripeSubscription } from "./components/StripeSubscription"
import React, { FC, useEffect, useState } from "react"
import { PlansData } from "./components/PlansData"
import usePageStyling from "src/hooks/usePageStyling"
import useAuthedUser from "src/hooks/useAuthedUser"
import { StripeSubscriptionPlans } from "./components/StripeSubscriptionPlans"
import { PlansPageStyling } from "src/context/types"
import { useIsMobileApp } from "src/util/deviceType"
import { FaCircleCheck } from "react-icons/fa6"
import { SectionContainer } from "src/components/common/SectionContainer"

export const Plans = () => {
  const { t } = useTranslation()
  const { screenSize } = useWindowSize()
  const { isSafariMobile, isAndroid } = useBrowserType()
  const isMobile = useIsMobileApp()
  const isLandscape = screenSize.width > screenSize.height
  const params = useSearchParams<{
    success?: true | undefined
    canceled?: true | undefined
  }>()
  const { authedUser, getAuthedUser } = useAuthedUser()
  const cameFromCheckoutSuccess: boolean = params.success
  // const cameFromCheckoutCancel: boolean = params.canceled
  const [hasCancelled, setHasCancelled] = React.useState(null)
  const [
    returnToHashStorage,
    returnToHashParseStorage,
    setReturnToHash,
    clearReturnToHash
  ] = useLocalStorage("returnToHash")
  const returnToHash =
    returnToHashStorage != null ? returnToHashParseStorage["roomHash"] : null

  // const roomStyles = useRecoilValue(brandingSelector)
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const [mobilePurchaseJustCompleted, setMobilePurchaseJustCompleted] =
    useRecoilState(mobilePurchaseJustCompletedAtom)
  const room = useRecoilValue(roomSelector)

  const [plansData, setPlansData] = React.useState<PlansData>(null)

  const history = useHistory()
  const meetingClient = React.useContext(SDKContext)
  const [loadingRoomHash, setLoadingRoomHash] =
    useRecoilState(loadingRoomHashAtom)
  const [loading, setLoading] = React.useState(true)
  const [joinButtonLoading, setJoinButtonLoading] = useRecoilState(
    joinButtonLoadingSelector
  )

  const { validateRoom } = useValidateRoom(meetingClient)
  const [view, setView] = useRecoilState(viewSelector)

  const fetchPlansData = async (showLoading = false) => {
    setLoading(showLoading)
    const res = await axios.post(
      meetingClient?.envConfig.PLANS,
      {
        host: meetingClient?.envConfig.DOMAIN_HOSTNAME
      },
      {
        withCredentials: true
      }
    )
    setPlansData(Object.keys(res.data).length === 0 ? null : res.data)
    console.log(res.data)
    setLoading(false)
  }

  React.useEffect(() => {
    if (meetingClient) {
      fetchPlansData(true)
    }
  }, [meetingClient])

  useEffect(() => {
    getAuthedUser()
  }, [])

  const handleOpenRoom = async () => {
    setMobilePurchaseJustCompleted(false)
    // Disconnect the previous meeting if active
    setJoinButtonLoading(false)
    await meetingClient?.disconnectGateway(0)

    meetingClient.setRoomHash(returnToHash)
    setView(Views.LOADING)
    setLoadingRoomHash(returnToHash)

    const joinRoom = !!authedUser?.screenName
    if (joinRoom) meetingClient?.setUserName(authedUser?.screenName)
    await validateRoom({ joinRoomDirectly: joinRoom })

    // If we are not join room direct then navigate to home page, So that user can set userName
    navigateToRoomHash(returnToHash)
    setView(Views.HOME)
  }

  const navigateToRoomHash = (roomhash: string) => {
    history.replace(addCurrentQueryParams(roomhash))
  }

  const isMobileApp = useIsMobileApp()

  const isMobileSubscription =
    !cameFromCheckoutSuccess && !!plansData?.stripeSubscriptionId

  const renderBody = () => {
    // show loading state
    if (loading)
      return (
        <Stack align={"center"}>
          <CircularProgress
            isIndeterminate
            value={30}
            color={plansPageStyles?.planPagePanelTextColor ?? "orange.400"}
            thickness="12px"
          />
        </Stack>
      )
    // First we determine whether the user has a subscription or not
    const hasActiveSubscription =
      (plansData != null && plansData.entitled) || cameFromCheckoutSuccess

    if (mobilePurchaseJustCompleted) {
      return (
        <Flex direction={"column"} alignItems={"center"}>
          <Box background={"white"} borderRadius="9999px">
            <Box margin="-2px">
              <FaCircleCheck size="60px" color="green" />
            </Box>
          </Box>
          <Text
            color={plansPageStyles?.planPagePanelSubTextColor}
            textAlign={"center"}
            paddingTop="10px"
          >
            Your subscription purchase was successful
          </Text>
          <PlansButton
            isMobile={isMobile}
            isLandscape={isLandscape}
            onClick={() => {
              setMobilePurchaseJustCompleted(false)
              localStorage.setItem("avatarsTabIndex", JSON.stringify(0))
              history.replace(Routes.AVATARS)
            }}
          >
            {t("Explore")}
          </PlansButton>
        </Flex>
      )
    }

    if (hasActiveSubscription) {
      if (
        (isMobileSubscription && !isMobile) ||
        (!isMobileSubscription && isMobile)
      ) {
        return (
          <>
            <RemainingTime plansData={plansData} />
            <div style={{ padding: "5px" }} />
            <Text
              color={plansPageStyles?.planPagePanelSubTextColor}
              textAlign={"center"}
            >
              You have an active subscription, please manage it on{" "}
              {isMobile ? "a desktop browser." : "your mobile device."}
            </Text>
          </>
        )
      }

      if (isMobileSubscription) {
        return (
          <>
            <RemainingTime plansData={plansData} />
            <div style={{ padding: "5px" }} />
            <MobileSubscription plansData={plansData} />
          </>
        )
      } else {
        return (
          <>
            {!cameFromCheckoutSuccess && (
              <RemainingTime plansData={plansData} />
            )}

            <StripeSubscription
              cameFromCheckoutSuccess={cameFromCheckoutSuccess}
              hasCancelled={hasCancelled}
              plansData={plansData}
              setHasCancelled={setHasCancelled}
              fetchPlansData={fetchPlansData}
              setLoading={setLoading}
            />
          </>
        )
      }
    } else {
      // no active subscription

      return (
        <Flex direction={"column"} gap="20px">
          {!cameFromCheckoutSuccess && <RemainingTime plansData={plansData} />}

          <TimeUntilUTCMidnight />

          {/* {!isMobile && (
            <StripeSubscriptionPlans
              fetchPlansData={fetchPlansData}
              plansData={plansData}
            />
          )} */}

          {isMobile && (
            <PlansButton
              onClick={async () => {
                if (isMobile) {
                  meetingClient?.nativeBridge.eventShowMobilePaywall()
                }
              }}
              isMobile={isMobile}
              isLandscape={isLandscape}
              buttonText={t("Extend Access")}
            />
          )}
        </Flex>
      )
    }
  }

  return (
    <PlansLayout>
      <SectionContainer>
        <Flex direction={"column"} gap="5px">
          {renderBody()}
        </Flex>
      </SectionContainer>

      {!isMobileSubscription && !cameFromCheckoutSuccess && !isMobileApp && (
        <StripeSubscriptionPlans
          fetchPlansData={fetchPlansData}
          plansData={plansData}
        />
      )}

      {/* {returnToHash &&
        (mobilePurchaseJustCompleted || cameFromCheckoutSuccess) && (
          <PlansButton
            onClick={async () => {
              // navigate to room
              handleOpenRoom()
              clearReturnToHash()
            }}
            isMobile={isMobile}
            isLandscape={isLandscape}
            buttonText={t("Return to room")}
          />
        )} */}
    </PlansLayout>
  )
}

const RemainingTime = ({ plansData }: { plansData: PlansData }) => {
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")
  const size = useWindowSize()
  const params = useSearchParams<{
    success?: true | undefined
    canceled?: true | undefined
  }>()
  const cameFromCheckoutSuccess: boolean = params.success

  const hasActiveSubscription =
    (plansData != null && plansData.entitled) || cameFromCheckoutSuccess
  const hasFreeTimeRemaining =
    plansData?.freeTimeRemainingSeconds != null &&
    plansData?.freeTimeRemainingSeconds > 0
  const hasPaidTimeRemaining =
    plansData?.paidTimeRemainingSeconds != null &&
    plansData?.paidTimeRemainingSeconds > 0
  const hasUnlimitedPaidTime = !!plansData?.hasUnlimitedPaidTime

  if (hasUnlimitedPaidTime && hasActiveSubscription) {
    return (
      <>
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          You have unlimited time.
        </Text>
      </>
    )
  } else if (hasPaidTimeRemaining && hasActiveSubscription) {
    // display paid time remaining
    const time = getHoursMinutesSeconds(plansData?.paidTimeRemainingSeconds)
    return (
      <Flex direction={"column"} alignItems="center">
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          You have
        </Text>
        <HoursMinutesSeconds {...time} />
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          of daily time remaining.
        </Text>
      </Flex>
    )
  } else if (hasFreeTimeRemaining && !hasActiveSubscription) {
    // display free time remaining
    const time = getHoursMinutesSeconds(plansData?.freeTimeRemainingSeconds)
    return (
      <Flex direction={"column"} alignItems="center">
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          You have
        </Text>
        <HoursMinutesSeconds {...time} />
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          of free daily time remaining.
        </Text>
      </Flex>
    )
  }
  // }
  else {
    return (
      <Flex direction={"column"} alignItems="center">
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          You have no time remaining.
        </Text>
        <div style={{ padding: "5px" }} />
        <Text
          color={plansPageStyles?.planPagePanelSubTextColor}
          textAlign={"center"}
        >
          Your time allowance will reset tomorrow, consider upgrading your plan
          to gain a higher allowance.
        </Text>
      </Flex>
    )
  }
}

const getHoursMinutesSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  seconds %= 3600
  const minutes = Math.floor(seconds / 60)
  const s = seconds % 60
  return {
    hours,
    minutes,
    seconds: s
  }
}

const TimeUntilUTCMidnight = () => {
  const size = useWindowSize()
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")

  useEffect(() => {
    const intervalId = setInterval(() => setTimeRemaining(updateTime()), 1000)
    return () => clearInterval(intervalId)
  })

  const updateTime = () => {
    const now = new Date()
    // Get the next UTC midnight
    const dateUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    )
    const midnightUTC = new Date(dateUTC.getTime() + 24 * 60 * 60 * 1000)

    // Calculate remaining milliseconds and then convert to seconds
    const remainingMilliseconds = +midnightUTC - +now
    const remainingSeconds = Math.floor(remainingMilliseconds / 1000)
    const hms = getHoursMinutesSeconds(remainingSeconds)
    return hms
  }

  const [timeRemaining, setTimeRemaining] = useState(updateTime())

  return (
    <Flex direction={"column"} alignItems="center">
      <Text
        whiteSpace={"pre-wrap"}
        color={plansPageStyles?.planPagePanelSubTextColor}
        textAlign={"center"}
      >
        Free time will reset in:
      </Text>
      <HoursMinutesSeconds {...timeRemaining} />
    </Flex>
  )
}

const HoursMinutesSeconds = ({
  hours,
  minutes,
  seconds
}: {
  hours: number
  minutes: number
  seconds: number
}) => {
  const plansPageStyles: PlansPageStyling = usePageStyling("plansPageStyling")

  return (
    <Flex>
      <Text
        color={plansPageStyles?.planPagePanelSubTextColor}
        fontWeight={"bold"}
      >
        {hours.toString().padStart(2, "0")}
      </Text>
      <Text color={plansPageStyles?.planPagePanelSubTextColor}>h:</Text>
      <Text
        color={plansPageStyles?.planPagePanelSubTextColor}
        fontWeight={"bold"}
      >
        {minutes.toString().padStart(2, "0")}
      </Text>
      <Text color={plansPageStyles?.planPagePanelSubTextColor}>m:</Text>
      <Text
        color={plansPageStyles?.planPagePanelSubTextColor}
        fontWeight={"bold"}
      >
        {seconds.toString().padStart(2, "0")}
      </Text>
      <Text color={plansPageStyles?.planPagePanelSubTextColor}>s</Text>
    </Flex>
  )
}
